import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { registerPush } from "@/lib/push";

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const msgListeners = useRef(new Set());
  const signalListeners = useRef(new Set());
  const readListeners = useRef(new Set());
  const typingListeners = useRef(new Set());
  const superLikeListeners = useRef(new Set());
  const giftListeners = useRef(new Set());
  const goalListeners = useRef(new Set());
  const privateListeners = useRef(new Set());
  const liveListeners = useRef(new Set());
  const adminListeners = useRef(new Set());
  const queueRef = useRef([]);
  const [matchUser, setMatchUser] = useState(null);
  const [connected, setConnected] = useState(false);

  const showMatch = useCallback((u) => u && setMatchUser(u), []);
  const clearMatch = useCallback(() => setMatchUser(null), []);

  const addMessageListener = useCallback((fn) => {
    msgListeners.current.add(fn);
    return () => msgListeners.current.delete(fn);
  }, []);

  const addSignalListener = useCallback((fn) => {
    signalListeners.current.add(fn);
    return () => signalListeners.current.delete(fn);
  }, []);

  const addReadListener = useCallback((fn) => {
    readListeners.current.add(fn);
    return () => readListeners.current.delete(fn);
  }, []);

  const addTypingListener = useCallback((fn) => {
    typingListeners.current.add(fn);
    return () => typingListeners.current.delete(fn);
  }, []);

  const addSuperLikeListener = useCallback((fn) => {
    superLikeListeners.current.add(fn);
    return () => superLikeListeners.current.delete(fn);
  }, []);

  const addGiftListener = useCallback((fn) => {
    giftListeners.current.add(fn);
    return () => giftListeners.current.delete(fn);
  }, []);

  const addGoalListener = useCallback((fn) => {
    goalListeners.current.add(fn);
    return () => goalListeners.current.delete(fn);
  }, []);

  const addPrivateListener = useCallback((fn) => {
    privateListeners.current.add(fn);
    return () => privateListeners.current.delete(fn);
  }, []);

  const addLiveListener = useCallback((fn) => {
    liveListeners.current.add(fn);
    return () => liveListeners.current.delete(fn);
  }, []);

  const addAdminListener = useCallback((fn) => {
    adminListeners.current.add(fn);
    return () => adminListeners.current.delete(fn);
  }, []);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(obj));
    } else {
      // Socket isn't open yet (initial connect / mid-reconnect) — queue it and
      // flush once the connection opens, instead of silently dropping it.
      queueRef.current.push(obj);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    registerPush();
    let alive = true;
    let reconnectTimer;

    const connect = () => {
      if (!alive) return;
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/api/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        const queued = queueRef.current;
        queueRef.current = [];
        queued.forEach((obj) => ws.send(JSON.stringify(obj)));
      };

      ws.onmessage = async (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === "message") {
            msgListeners.current.forEach((fn) => fn(data.message));
          } else if (data.type === "signal") {
            signalListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "read") {
            readListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "typing") {
            typingListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "superlike") {
            superLikeListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "gift") {
            giftListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "goal") {
            goalListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "private" || data.type === "private_invite" || data.type === "private_guest" || data.type === "private_invite_consent") {
            privateListeners.current.forEach((fn) => fn(data));
          } else if (["live_viewer_joined", "live_viewer_left", "live_viewer_count", "live_ended", "live_chat"].includes(data.type)) {
            liveListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "support_ticket") {
            adminListeners.current.forEach((fn) => fn(data));
          } else if (data.type === "match" && data.user_id) {
            const { data: prof } = await api.get(`/profiles/${data.user_id}`);
            setMatchUser(prof);
          }
        } catch { /* ignore */ }
      };

      ws.onclose = () => { setConnected(false); if (alive) reconnectTimer = setTimeout(connect, 2500); };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      alive = false;
      setConnected(false);
      clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [user]);

  return (
    <SocketContext.Provider value={{ addMessageListener, addSignalListener, addReadListener, addTypingListener, addSuperLikeListener, addGiftListener, addGoalListener, addPrivateListener, addLiveListener, addAdminListener, send, showMatch, matchUser, clearMatch, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
